import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import KnowledgeExplorer from './KnowledgeExplorer'
import {
  Unlock, FileText, CheckCircle, AlertCircle,
  ChevronRight, Search, MessageSquare, GitBranch, BookOpen,
  Hexagon, Shield, Mail, ScanLine, Eye, EyeOff, RefreshCw,
  ArrowLeft, Sparkles, Building2, Edit3, Copy, Check, X, Paperclip,
  Download, Brain, Scale, Ruler, Map as MapIcon, Workflow, Package,
  Users, Cpu, KeyRound, Network, Inbox, Puzzle, ArrowUp, Pin,
  Trash2, RotateCcw, Layers3, FolderOpen, Plus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { getToken } from '@/lib/auth'

const ARCH_PIN = '2606'

// The three ecosystem folders explicitly kept outside the new "Architecture"
// wrapper, per direct instruction -- they were never part of Dervent's
// original eleven-category list, so they stay as their own, separate,
// sibling-level spaces rather than nested inside Architecture.
const ECOSYSTEM_PROJECT_IDS = ['arch-cora', 'arch-mailorganiser', 'arch-workerscan']

const API = (path: string, init?: RequestInit) =>
  fetch(`/api/dashboard${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
      ...((init?.headers as Record<string, string>) ?? {}),
    },
  }).then(r => r.json())

interface ArchProject {
  id: string
  name: string
  description: string
  locked: number
  color: string
  icon: string
  doc_count: number
}

interface ArchDoc {
  id: string
  filename: string
  title: string
  version: string
  status: 'approved' | 'draft' | 'in_progress' | 'superseded'
  last_modified: string | null
  last_approved: string | null
  approved: number
  summary: string | null
  tags: string
  pinned?: number
}

const PROJECT_ICONS: Record<string, typeof Hexagon> = {
  hexagon: Hexagon,
  shield: Shield,
  mail: Mail,
  scan: ScanLine,
  // Added when each category got its own icon rather than sharing the
  // Hexagon fallback -- one distinct icon per category, chosen for a real,
  // legible association rather than arbitrarily.
  'git-branch': GitBranch,   // Decisions -- branching points
  puzzle: Puzzle,            // Capabilities -- composable pieces
  brain: Brain,              // Intelligence
  scale: Scale,              // Governance -- rules, standards
  ruler: Ruler,               // Blueprints -- technical/structural design
  map: MapIcon,               // Roadmap
  workflow: Workflow,        // Automation Pipeline
  package: Package,          // OPS Modules -- discrete built units
  users: Users,               // Multi-Tenants
  cpu: Cpu,                   // AI Models -- distinct from Intelligence's Brain
  key: KeyRound,              // Bring Your Own Key
  network: Network,          // CORA -- distinct from Architecture's own Building2
  inbox: Inbox,               // Unfiled
}

// Draft/In Progress/Completed scheme, agreed directly with Dervent: gray for
// draft (a lower-energy "not yet started" state, not a warm color), amber for
// in_progress, green for completed/approved. Superseded is kept as its own
// distinct state rather than folded into draft -- a doc that's been replaced
// by a newer one is a genuinely different thing from one that's just new.
function StatusBadge({ status }: { status: ArchDoc['status'] }) {
  const cfg = {
    approved:    { label: 'Completed',   cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', dot: 'bg-emerald-500' },
    in_progress: { label: 'In Progress', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20', dot: 'bg-amber-500' },
    draft:       { label: 'Draft',       cls: 'bg-slate-500/10 text-slate-400 border-slate-500/20', dot: 'bg-slate-500' },
    superseded:  { label: 'Superseded',  cls: 'bg-surface-elevated text-text-muted border-border', dot: 'bg-text-muted' },
  }
  const s = cfg[status] ?? cfg.draft
  return (
    <>
      {/* Mobile: a plain dot, since the full text badge was eating so much
          width in the 2-column grid that document titles truncated to a
          single letter. Desktop keeps the full badge, per Dervent's own
          instruction, since there's genuinely more room there. */}
      <span className={cn('inline-block sm:hidden w-2 h-2 rounded-full shrink-0', s.dot)} title={s.label} aria-label={s.label} />
      <span className={cn('hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border', s.cls)}>
        {status === 'approved'    && <CheckCircle className="h-2.5 w-2.5" />}
        {status === 'in_progress' && <RefreshCw   className="h-2.5 w-2.5" />}
        {status === 'draft'       && <FileText    className="h-2.5 w-2.5" />}
        {status === 'superseded'  && <AlertCircle className="h-2.5 w-2.5" />}
        {s.label}
      </span>
    </>
  )
}

// Workspace-level PIN gate -- fires immediately when Architecture panel opens
function PinGate({ onAuth }: { onAuth: () => void }) {
  const [pin, setPin]       = useState('')
  const [show, setShow]     = useState(false)
  const [error, setError]   = useState('')
  const [shake, setShake]   = useState(false)

  function attempt() {
    if (pin === ARCH_PIN) {
      onAuth()
    } else {
      setError('Incorrect PIN')
      setShake(true)
      setPin('')
      setTimeout(() => { setShake(false); setError('') }, 900)
    }
  }

  return (
    <div className="vault-stage flex items-center justify-center min-h-[70dvh] w-full">
      <style>{`
        @keyframes arch-shake {
          0%,100% { transform: translateX(0); }
          20%,60%  { transform: translateX(-10px); }
          40%,80%  { transform: translateX(10px); }
        }
        .arch-shake { animation: arch-shake 0.4s ease-in-out; }
      `}</style>
      <div className={cn(
        'vault-card w-full max-w-[440px] text-center',
        shake && 'arch-shake'
      )}>
        <div className="vault-eyebrow"><span /> PRIVATE WORKSPACE</div>
        <div className="vault-object" aria-hidden="true"><div className="vault-door"><span className="vault-hinge" /><span className="vault-dial"><span /></span></div></div>
        <h2 className="vault-title">Architecture Vault</h2>
        <p className="vault-description">The thinking behind everything you build.</p>
        <div className="vault-tags"><span>Constitution</span><span>Decisions</span><span>Blueprints</span></div>
        <label htmlFor="architecture-vault-pin" className="vault-pin-label">Your access PIN</label>

        <div className="relative mb-3">
          <input
            id="architecture-vault-pin"
            inputMode="numeric"
            aria-invalid={!!error}
            aria-describedby={error ? 'vault-pin-error' : undefined}
            type={show ? 'text' : 'password'}
            value={pin}
            onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
            onKeyDown={e => e.key === 'Enter' && pin && attempt()}
            placeholder="PIN"
            autoFocus
            className="vault-pin w-full rounded-2xl px-12 py-4 text-center font-mono text-2xl tracking-[0.5em] focus:outline-none"
          />
          <button
            type="button"
            aria-label={show ? 'Hide PIN' : 'Show PIN'}
            onClick={() => setShow(s => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary transition-colors cursor-pointer"
          >
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>

        {error && <p id="vault-pin-error" role="alert" className="text-red-400 text-xs mb-3 font-mono">{error}</p>}

        <button
          type="button"
          onClick={attempt}
          disabled={!pin}
          className="vault-unlock w-full py-4 rounded-2xl disabled:opacity-45 disabled:cursor-not-allowed text-white text-sm font-semibold transition-all cursor-pointer"
        >
          <Unlock className="h-4 w-4" aria-hidden="true" /> Open vault <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
        <p className="vault-footer">Your architecture. Your direction.</p>
      </div>
    </div>
  )
}

// --- Markdown rendering for document view ---
// Ported from ChatPanel.tsx's proven MarkdownContent pattern rather than
// extracting a shared module -- ChatPanel is large and high-stakes, and
// this component doesn't need to touch it to get the same real,
// already-working rendering (headers, bold, tables, images, code blocks
// with syntax highlighting, and a live iframe preview for embedded
// HTML/SVG). Deliberate duplication over refactor risk; a genuine future
// cleanup candidate, not urgent tonight.

function useDocCopy() {
  const [copied, setCopied] = useState(false)
  const copy = useCallback((text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [])
  return { copied, copy }
}

function DocCodeBlock({ code, language }: { code: string; language: string }) {
  const { copied, copy } = useDocCopy()
  return (
    <div className="relative my-3 rounded-lg overflow-hidden border border-border">
      <div className="flex items-center justify-between px-3 py-1.5 bg-surface-elevated border-b border-border">
        <span className="text-[10px] text-text-muted uppercase tracking-wider font-mono">{language}</span>
        <button type="button" onClick={() => copy(code)} className="flex items-center gap-1 text-[10px] text-text-muted hover:text-text-primary transition-colors cursor-pointer">
          {copied ? <><Check className="h-3 w-3 text-emerald-400" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
        </button>
      </div>
      <SyntaxHighlighter style={oneDark} language={language} PreTag="div" customStyle={{ margin: 0, borderRadius: 0, fontSize: '12px', whiteSpace: 'pre-wrap', overflowX: 'auto', maxWidth: '100%' }}>
        {code}
      </SyntaxHighlighter>
    </div>
  )
}

function DocMarkdownImage({ src, alt }: { src?: string; alt?: string }) {
  const [loaded, setLoaded] = useState(false)
  const [expanded, setExpanded] = useState(false)
  if (!src) return null
  return (
    <>
      <img
        key={src}
        src={src}
        alt={alt || 'Document image'}
        onLoad={() => setLoaded(true)}
        onClick={() => setExpanded(true)}
        className={cn(
          'max-w-full sm:max-w-[500px] rounded-xl border border-border hover:brightness-110 transition-[filter] cursor-pointer block my-3',
          loaded ? 'opacity-100' : 'opacity-0',
        )}
      />
      {expanded && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) setExpanded(false) }}
        >
          <img src={src} alt={alt || 'Document image'} className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg" />
          <div className="absolute top-4 right-5 flex items-center gap-3">
            <a href={src} download className="text-white/70 hover:text-white transition-colors" onClick={(e) => e.stopPropagation()}>
              <Download className="h-5 w-5" />
            </a>
            <button type="button" onClick={() => setExpanded(false)} className="text-white/70 hover:text-white transition-colors cursor-pointer">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

function DocHtmlContent({ html }: { html: string }) {
  return <iframe srcDoc={html} sandbox="allow-scripts" className="w-full min-h-[300px] rounded-xl border border-border my-3" title="Rendered diagram" />
}

function DocMarkdownContent({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        code({ className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '')
          const codeStr = String(children).replace(/\n$/, '')
          if (match && (match[1] === 'html' || match[1] === 'svg')) return <DocHtmlContent html={codeStr} />
          if (match) return <DocCodeBlock code={codeStr} language={match[1]} />
          return <code className="px-1.5 py-0.5 rounded bg-surface-elevated text-xs font-mono text-indigo-400" {...props}>{children}</code>
        },
        pre({ children }) { return <>{children}</> },
        img({ src, alt }) { return <DocMarkdownImage src={typeof src === 'string' ? src : undefined} alt={alt} /> },
        h1({ children }) { return <h1 className="text-2xl font-bold mt-6 mb-3 pb-2 border-b border-border text-text-primary">{children}</h1> },
        h2({ children }) { return <h2 className="text-xl font-semibold mt-5 mb-2 text-text-primary">{children}</h2> },
        h3({ children }) { return <h3 className="text-lg font-semibold mt-4 mb-2 text-text-primary">{children}</h3> },
        h4({ children }) { return <h4 className="text-base font-semibold mt-3 mb-1 text-text-secondary">{children}</h4> },
        p({ children }) { return <p className="mb-3 last:mb-0 leading-relaxed text-text-secondary">{children}</p> },
        ul({ children }) { return <ul className="list-disc pl-5 mb-3 space-y-1 text-text-secondary">{children}</ul> },
        ol({ children }) { return <ol className="list-decimal pl-5 mb-3 space-y-1 text-text-secondary">{children}</ol> },
        li({ children }) { return <li className="leading-relaxed">{children}</li> },
        table({ children }) {
          return <div className="overflow-x-auto my-3 rounded-lg border border-border"><table className="text-xs border-collapse w-full">{children}</table></div>
        },
        th({ children }) { return <th className="border-b border-border px-3 py-2 bg-surface-elevated text-left font-semibold text-text-primary">{children}</th> },
        td({ children }) { return <td className="border-b border-border/50 px-3 py-2 text-text-secondary">{children}</td> },
        blockquote({ children }) { return <blockquote className="border-l-2 border-indigo-500/40 pl-4 my-3 text-text-muted italic bg-indigo-500/5 py-2 pr-3 rounded-r-lg">{children}</blockquote> },
        hr() { return <hr className="my-6 border-border" /> },
        strong({ children }) { return <strong className="font-semibold text-text-primary">{children}</strong> },
        a({ children, href }) { return <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline">{children}</a> },
      }}
    >
      {content}
    </ReactMarkdown>
  )
}

function DocumentStatusToggle({ status, saving, onChange }: { status: ArchDoc['status']; saving: boolean; onChange: (status: 'draft' | 'in_progress' | 'approved') => void }) {
  const options = [
    { id: 'draft' as const, label: 'Draft', active: 'bg-slate-500 text-white shadow-sm' },
    { id: 'in_progress' as const, label: 'In Progress', active: 'bg-amber-500 text-white shadow-sm' },
    { id: 'approved' as const, label: 'Complete', active: 'bg-emerald-600 text-white shadow-sm' },
  ]
  return (
    <div className="inline-flex items-center rounded-xl border border-border bg-surface-elevated p-1 shadow-inner" aria-label="Document status">
      {options.map(option => (
        <button key={option.id} type="button" disabled={saving} onClick={() => onChange(option.id)}
          className={cn('px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all cursor-pointer disabled:opacity-50', status === option.id ? option.active : 'text-text-muted hover:text-text-primary hover:bg-surface')}>
          {option.label}
        </button>
      ))}
    </div>
  )
}

function DocViewer({ doc, onBack, onStatusChange }: { doc: ArchDoc; onBack: () => void; onStatusChange: (status: 'draft' | 'in_progress' | 'approved') => void }) {
  const [content, setContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [editContent, setEditContent] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [copied, setCopied] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [status, setStatus] = useState<ArchDoc['status']>(doc.status)
  const [statusSaving, setStatusSaving] = useState(false)
  const [statusError, setStatusError] = useState('')

  async function changeStatus(nextStatus: 'draft' | 'in_progress' | 'approved') {
    if (statusSaving || nextStatus === status) return
    const previous = status
    setStatus(nextStatus)
    setStatusSaving(true)
    setStatusError('')
    try {
      const r = await API(`/architecture/document/${doc.id}`, { method: 'PUT', body: JSON.stringify({ status: nextStatus }) })
      if (!r.success) throw new Error(r.error || 'Status update failed')
      onStatusChange(nextStatus)
    } catch (e) {
      setStatus(previous)
      setStatusError(e instanceof Error ? e.message : 'Status update failed')
    } finally { setStatusSaving(false) }
  }
  // Some documents are genuinely long, and the panel's own scroll container
  // lives outside this component entirely (in MainContent.tsx), so this
  // targets it via the explicit data-scroll-container attribute added
  // there, rather than a brittle class-name match. Only shown once
  // scrolled down a meaningful amount -- no point cluttering the view when
  // there's nothing to scroll back from.
  const [showBackToTop, setShowBackToTop] = useState(false)

  useEffect(() => {
    const container = document.querySelector('[data-scroll-container]')
    if (!container) return
    const onScroll = () => setShowBackToTop(container.scrollTop > 400)
    container.addEventListener('scroll', onScroll)
    onScroll()
    return () => container.removeEventListener('scroll', onScroll)
  }, [])

  function scrollToTop() {
    document.querySelector('[data-scroll-container]')?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    setLoading(true)
    API(`/architecture/document/${doc.id}`)
      .then(r => {
        const c = r.content ?? '# Content not available'
        setContent(c)
        setEditContent(c)
      })
      .catch(() => {
        setContent('# Failed to load document')
        setEditContent('')
      })
      .finally(() => setLoading(false))
  }, [doc.id])

  async function saveDoc() {
    if (!editContent.trim()) return
    setSaving(true)
    try {
      const r = await API(`/architecture/document/${doc.id}`, {
        method: 'PUT',
        body: JSON.stringify({ content: editContent }),
      })
      if (r.success) {
        setContent(editContent)
        setEditing(false)
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
      }
    } catch { /* silently */ }
    finally { setSaving(false) }
  }

  function copyDoc() {
    navigator.clipboard.writeText(content ?? '')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function deleteDoc() {
    setDeleting(true)
    try {
      const r = await API(`/architecture/document/${doc.id}`, { method: 'DELETE' })
      if (r.success) {
        onBack()
      }
    } catch { /* silently */ }
    finally { setDeleting(false); setConfirmingDelete(false) }
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <button type="button" onClick={onBack}
          className="flex items-center gap-1.5 text-text-muted hover:text-text-primary text-xs font-mono transition-colors cursor-pointer">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <span className="text-text-muted/50">·</span>
        <FileText className="h-4 w-4 text-indigo-400 shrink-0" />
        <span className="text-text-primary font-medium text-sm truncate flex-1">{doc.title}</span>
        <StatusBadge status={status} />
        <span className="text-text-muted text-xs font-mono shrink-0">v{doc.version}</span>
        {!loading && !editing && (
          <div className="flex items-center gap-1 ml-2">
            <button type="button" onClick={copyDoc}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-text-muted hover:text-text-primary hover:bg-white/5 text-xs transition-colors cursor-pointer" title="Copy content">
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
            <button type="button" onClick={() => setEditing(true)}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-text-muted hover:text-indigo-400 hover:bg-indigo-500/5 text-xs transition-colors cursor-pointer">
              <Edit3 className="h-3.5 w-3.5" /> Edit
            </button>
            <button type="button" onClick={() => setConfirmingDelete(true)}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-text-muted hover:text-red-400 hover:bg-red-500/5 text-xs transition-colors cursor-pointer" title="Move to trash">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {editing && (
          <div className="flex items-center gap-1.5 ml-2">
            <button type="button" onClick={() => { setEditing(false); setEditContent(content ?? '') }}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-text-muted hover:text-text-primary hover:bg-white/5 text-xs transition-colors cursor-pointer">
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
            <button type="button" onClick={saveDoc} disabled={saving}
              className="flex items-center gap-1 px-3 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-medium transition-colors cursor-pointer">
              {saving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 rounded-2xl border border-border bg-surface-elevated/60 px-4 py-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-text-muted mb-1">Document status</div>
          <div className="text-xs text-text-muted">Set the catalogue lifecycle state without changing the R2 document body.</div>
        </div>
        <DocumentStatusToggle status={status} saving={statusSaving} onChange={changeStatus} />
      </div>
      {statusError && <div className="mb-3 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">{statusError}</div>}
      {saved && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs">
          <Check className="h-3.5 w-3.5" /> Document saved
        </div>
      )}
      {confirmingDelete && (
        <div className="flex items-center gap-3 mb-3 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-sm">
          <span className="text-red-400 flex-1">Move "{doc.title}" to trash? You can restore it from the Trash folder anytime, or empty trash to remove it permanently.</span>
          <button type="button" onClick={() => setConfirmingDelete(false)}
            className="px-3 py-1 rounded-lg text-text-muted hover:text-text-primary hover:bg-white/5 text-xs transition-colors cursor-pointer">
            Cancel
          </button>
          <button type="button" onClick={deleteDoc} disabled={deleting}
            className="flex items-center gap-1 px-3 py-1 rounded-lg bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-medium transition-colors cursor-pointer">
            {deleting ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            {deleting ? 'Moving…' : 'Move to trash'}
          </button>
        </div>
      )}
      {loading ? (
        <div className="flex items-center gap-2 text-text-muted text-sm py-12 justify-center">
          <RefreshCw className="h-4 w-4 animate-spin" /> Loading...
        </div>
      ) : editing ? (
        <textarea
          value={editContent}
          onChange={e => setEditContent(e.target.value)}
          className="w-full min-h-[500px] bg-surface border border-border rounded-xl p-5 text-sm text-text-secondary font-mono leading-relaxed focus:outline-none focus:border-indigo-500/40 resize-none"
        />
      ) : (
        <div className="w-full rounded-2xl bg-slate-200/70 dark:bg-black/20 border border-border p-3 sm:p-6 lg:p-10 overflow-x-auto">
          <article className="mx-auto w-full max-w-[900px] min-h-[900px] bg-white text-slate-700 border border-slate-200 rounded-sm shadow-[0_18px_55px_rgba(15,23,42,0.14)] px-5 py-8 sm:px-10 sm:py-12 lg:px-16 lg:py-14 [&_h1]:!text-slate-950 [&_h1]:!border-slate-200 [&_h2]:!text-blue-800 [&_h3]:!text-slate-900 [&_h4]:!text-slate-800 [&_p]:!text-slate-700 [&_li]:!text-slate-700 [&_strong]:!text-slate-950 [&_blockquote]:!text-blue-900 [&_blockquote]:!bg-blue-50 [&_blockquote]:!border-blue-500 [&_th]:!bg-slate-100 [&_th]:!text-slate-900 [&_td]:!text-slate-700 [&_a]:!text-blue-700 [&_hr]:!border-slate-200">
            <DocMarkdownContent content={content ?? ''} />
          </article>
        </div>
      )}

      {showBackToTop && (
        <button
          type="button"
          onClick={scrollToTop}
          aria-label="Back to top"
          className="fixed bottom-6 right-6 z-50 w-10 h-10 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white shadow-xl flex items-center justify-center transition-colors cursor-pointer"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

const DECISION_GROUPS = [
  { name: 'Foundational & Governance', description: 'Core principles, governance, and platform foundations', ids: [1, 2, 3, 5, 6], icon: Shield },
  { name: 'Platform Architecture', description: 'Platform services, core components and infrastructure', ids: [7, 8, 10, 11, 12, 14, 15, 16, 17, 20, 21, 22], icon: Network },
  { name: 'Intelligence', description: 'AI, model access and intelligent capabilities', ids: [4, 9, 24], icon: Brain },
  { name: 'Identity & Capability', description: 'Authentication, authorization and capability management', ids: [18, 19, 23, 25, 27], icon: Users },
  { name: 'Tenant Architecture', description: 'Tenant-specific architecture and configuration', ids: [13, 28, 29], icon: Package },
  { name: 'Integration / Commercial', description: 'External integrations and commercial architecture', ids: [26, 30], icon: Workflow },
] as const

function decisionNumber(doc: ArchDoc) {
  const match = `${doc.title} ${doc.filename}`.match(/AD[-_ ]?0*(\d{1,3})/i)
  return match ? Number(match[1]) : null
}

function decisionLabel(doc: ArchDoc) {
  const n = decisionNumber(doc)
  return n == null ? 'AD' : `AD-${String(n).padStart(3, '0')}`
}

function cleanDecisionTitle(doc: ArchDoc) {
  return doc.title.replace(/^AD[-_ ]?\d+\s*(?:--|—|–|:|-)?\s*/i, '').trim() || doc.title
}

function DecisionStatusBadge({ status }: { status: ArchDoc['status'] }) {
  const cfg = {
    approved: { label: 'Complete', dot: 'bg-emerald-500', cls: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
    in_progress: { label: 'In Progress', dot: 'bg-amber-500', cls: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
    draft: { label: 'Draft', dot: 'bg-slate-400', cls: 'bg-slate-500/10 text-slate-500 dark:text-slate-300 border-slate-500/20' },
    superseded: { label: 'Superseded', dot: 'bg-slate-500', cls: 'bg-slate-500/10 text-slate-500 border-slate-500/20' },
  }
  const s = cfg[status] ?? cfg.draft
  return <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[10px] font-semibold whitespace-nowrap', s.cls)}><span className={cn('h-2 w-2 rounded-full', s.dot)} />{s.label}</span>
}

function ProjectView({ project, onBack }: { project: ArchProject; onBack: () => void }) {
  const [docs, setDocs]             = useState<ArchDoc[]>([])
  const [loading, setLoading]       = useState(true)
  const [search, setSearch]         = useState('')
  const [filter, setFilter]         = useState<'all' | 'approved' | 'draft' | 'in_progress'>('all')
  const [activeDoc, setActiveDoc]   = useState<ArchDoc | null>(null)
  const [chatOpen, setChatOpen]     = useState(false)
  const [chatMsg, setChatMsg]       = useState('')
  const [chatHistory, setChatHistory] = useState<{ role: string; content: string }[]>([])
  const [chatLoading, setChatLoading] = useState(false)
  const [chatContextDoc, setChatContextDoc] = useState<{ id: string; title: string } | null>(null)
  const [showDocPicker, setShowDocPicker] = useState(false)
  const [loadingDocCtx, setLoadingDocCtx] = useState(false)
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null)
  const docPickerRef = useRef<HTMLDivElement>(null)
  const [confirmingEmpty, setConfirmingEmpty] = useState(false)
  const [emptying, setEmptying] = useState(false)
  const [decisionSort, setDecisionSort] = useState<'number' | 'title'>('number')
  const [collapsedDecisionGroups, setCollapsedDecisionGroups] = useState<Record<string, boolean>>({})
  const isTrash = project.id === 'arch-trash'
  const isDecisionProject = project.id === 'arch-pad' || project.id === 'arch-tad'

  async function emptyTrash() {
    setEmptying(true)
    try {
      const r = await API('/architecture/trash/empty', { method: 'DELETE' })
      if (r.success) {
        setDocs([])
        setConfirmingEmpty(false)
      }
    } catch { /* silently */ }
    finally { setEmptying(false) }
  }

  async function restoreDoc(doc: ArchDoc) {
    try {
      const r = await API(`/architecture/document/${doc.id}/restore`, { method: 'POST' })
      if (r.success) {
        setDocs(prev => prev.filter(d => d.id !== doc.id))
      }
    } catch { /* silently */ }
  }

  useEffect(() => {
    setLoading(true)
    API(`/architecture/project/${project.id}/documents`)
      .then(r => setDocs(r.documents ?? []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false))
  }, [project.id])

  // Close doc picker on outside click
  useEffect(() => {
    if (!showDocPicker) return
    function handler(e: MouseEvent) {
      if (docPickerRef.current && !docPickerRef.current.contains(e.target as Node)) {
        setShowDocPicker(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showDocPicker])

  // A category's own anchor/explainer document is pinned to always sort
  // first, per direct request -- "the anchor for every other document"
  // pattern, not just alphabetical or creation order.
  const filtered = docs.filter(d => {
    if (filter !== 'all' && d.status !== filter) return false
    if (search) {
      const q = search.toLowerCase()
      if (!d.title.toLowerCase().includes(q) && !d.filename.toLowerCase().includes(q) && !(d.summary ?? '').toLowerCase().includes(q)) return false
    }
    return true
  }).sort((a, b) => {
    if ((b.pinned ?? 0) !== (a.pinned ?? 0)) return (b.pinned ?? 0) - (a.pinned ?? 0)
    if (isDecisionProject && decisionSort === 'number') return (decisionNumber(a) ?? 9999) - (decisionNumber(b) ?? 9999)
    return a.title.localeCompare(b.title)
  })

  function loadDocCtx(doc: ArchDoc) {
    setShowDocPicker(false)
    setLoadingDocCtx(false)
    setChatContextDoc({ id: doc.id, title: doc.title })
  }

  async function sendChat() {
    if (!chatMsg.trim() || chatLoading) return
    const msg = chatMsg.trim()
    setChatMsg('')
    setChatLoading(true)
    setShowDocPicker(false)
    setChatHistory(h => [...h, { role: 'user', content: msg }])
    try {
      const history = chatHistory.slice(-8)
      const r = await API('/architecture/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: msg,
          document_id: chatContextDoc?.id ?? null,
          history,
        }),
      })
      setChatHistory(h => [...h, { role: 'assistant', content: r.response ?? 'No response.' }])
    } catch {
      setChatHistory(h => [...h, { role: 'assistant', content: 'Chat unavailable right now.' }])
    } finally {
      setChatLoading(false)
    }
  }

  function copyMsg(content: string, idx: number) {
    navigator.clipboard.writeText(content)
    setCopiedIdx(idx)
    setTimeout(() => setCopiedIdx(null), 2000)
  }

  const ProjectIcon = PROJECT_ICONS[project.icon] ?? Hexagon

  function applyLocalStatus(status: 'draft' | 'in_progress' | 'approved') {
    if (!activeDoc) return
    const id = activeDoc.id
    setDocs(prev => prev.map(d => d.id === id ? { ...d, status, approved: status === 'approved' ? 1 : 0 } : d))
    setActiveDoc(prev => prev ? { ...prev, status, approved: status === 'approved' ? 1 : 0 } : prev)
  }

  if (activeDoc) return <DocViewer doc={activeDoc} onBack={() => setActiveDoc(null)} onStatusChange={applyLocalStatus} />

  const FILTERS = [
    { id: 'all',         label: 'All' },
    { id: 'approved',    label: 'Completed' },
    { id: 'in_progress', label: 'In Progress' },
    { id: 'draft',       label: 'Draft' },
  ] as const

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button type="button" onClick={onBack}
          className="flex items-center gap-1.5 text-text-muted hover:text-text-primary text-xs font-mono transition-colors cursor-pointer">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <span className="text-text-muted/50">·</span>
        <div className="w-6 h-6 rounded border flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${project.color}20`, borderColor: `${project.color}30` }}>
          <ProjectIcon className="h-3.5 w-3.5" style={{ color: project.color }} />
        </div>
        <span className="text-text-primary font-semibold text-sm">{project.name}</span>
        <span className="ml-auto text-[10px] font-mono text-text-muted">{docs.length} docs</span>
        {isTrash && docs.length > 0 && (
          <button type="button" onClick={() => setConfirmingEmpty(true)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-red-400 hover:bg-red-500/10 text-xs font-medium transition-colors cursor-pointer">
            <Trash2 className="h-3.5 w-3.5" /> Empty Trash
          </button>
        )}
      </div>

      {isTrash && confirmingEmpty && (
        <div className="flex items-center gap-3 mb-4 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-sm">
          <span className="text-red-400 flex-1">Permanently delete all {docs.length} document{docs.length === 1 ? '' : 's'} in trash? This cannot be undone.</span>
          <button type="button" onClick={() => setConfirmingEmpty(false)}
            className="px-3 py-1 rounded-lg text-text-muted hover:text-text-primary hover:bg-white/5 text-xs transition-colors cursor-pointer">
            Cancel
          </button>
          <button type="button" onClick={emptyTrash} disabled={emptying}
            className="flex items-center gap-1 px-3 py-1 rounded-lg bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-medium transition-colors cursor-pointer">
            {emptying ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            {emptying ? 'Deleting…' : 'Delete permanently'}
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border mb-4">
        {[
          { id: 'docs',      label: 'Documents', icon: FileText },
          { id: 'decisions', label: 'Decisions', icon: GitBranch },
          { id: 'chat',      label: 'Chat',      icon: MessageSquare },
        ].map(t => (
          <button key={t.id} type="button"
            onClick={() => setChatOpen(t.id === 'chat' ? !chatOpen : false)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors cursor-pointer',
              (t.id === 'chat' ? chatOpen : !chatOpen && t.id === 'docs')
                ? 'border-indigo-500 text-indigo-400'
                : 'border-transparent text-text-muted hover:text-text-secondary'
            )}>
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {chatOpen ? (
        <div>
          <div className="space-y-3 mb-4 min-h-[200px]">
            {chatHistory.length === 0 && (
              <div className="text-center py-10">
                <Sparkles className="h-7 w-7 mx-auto mb-3 text-indigo-400/30" />
                <p className="text-text-muted text-sm">Ask anything about the architecture.</p>
                <p className="text-text-muted/60 text-xs mt-1">Answers from approved documents only.</p>
              </div>
            )}
            {chatHistory.map((m, i) => (
              <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                <div className={cn(m.role !== 'user' ? 'group max-w-[85%]' : 'max-w-[85%]')}>
                  <div className={cn('rounded-xl px-4 py-2.5 text-sm',
                    m.role === 'user'
                      ? 'bg-indigo-600/30 text-text-primary border border-indigo-500/20'
                      : 'bg-surface-elevated text-text-secondary border border-border'
                  )}>{m.content}</div>
                  {m.role === 'assistant' && (
                    <div className="flex gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button type="button" onClick={() => copyMsg(m.content, i)}
                        className="p-1 rounded text-text-muted hover:text-text-primary hover:bg-white/5 transition-colors cursor-pointer" title="Copy">
                        {copiedIdx === i ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex">
                <div className="bg-surface-elevated border border-border rounded-xl px-4 py-3">
                  <RefreshCw className="h-4 w-4 text-text-muted animate-spin" />
                </div>
              </div>
            )}
          </div>
          {/* Document context chip + picker */}
          <div className="flex items-center gap-2 mb-2">
            {chatContextDoc ? (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-xs text-indigo-400">
                <FileText className="h-3 w-3 shrink-0" />
                <span className="truncate max-w-[160px]">{chatContextDoc.title}</span>
                <button type="button" onClick={() => setChatContextDoc(null)}
                  className="ml-0.5 hover:text-white transition-colors cursor-pointer">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div ref={docPickerRef} className="relative">
                <button type="button" onClick={() => setShowDocPicker(s => !s)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-text-muted hover:text-text-secondary border border-border text-xs transition-colors cursor-pointer">
                  {loadingDocCtx ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />}
                  <span>Add context</span>
                </button>
                {showDocPicker && docs.length > 0 && (
                  <div className="absolute bottom-full mb-1.5 left-0 w-64 bg-surface-elevated border border-border rounded-xl shadow-xl py-1.5 z-10">
                    <p className="text-text-muted text-[10px] font-medium uppercase tracking-wide px-3 pt-1 pb-1.5">Load document as context</p>
                    {docs.slice(0, 8).map(d => (
                      <button key={d.id} type="button" onClick={() => loadDocCtx(d)}
                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-white/5 transition-colors cursor-pointer">
                        <FileText className="h-3 w-3 text-indigo-400/60 shrink-0" />
                        <span className="truncate">{d.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <input value={chatMsg} onChange={e => setChatMsg(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat()}
              placeholder="Ask the architecture..."
              className="flex-1 bg-surface-elevated border border-border rounded-xl px-4 py-2.5 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-indigo-500/40" />
            <button type="button" onClick={sendChat} disabled={!chatMsg.trim() || chatLoading}
              className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-30 text-white text-sm font-medium transition-colors cursor-pointer">
              Send
            </button>
          </div>
        </div>
      ) : (
        <div>
          {/* Search, full width on its own row, per direct request -- was
              sharing a row with four filter buttons, leaving it too narrow
              to be usable on mobile. */}
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={isDecisionProject ? 'Search decisions by number, title, or keyword...' : 'Search documents...'}
              className="w-full bg-surface-elevated/90 border border-border rounded-xl pl-9 pr-4 py-3 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-indigo-500/40 shadow-sm" />
          </div>

          {/* Filters, own row below search */}
          <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-0.5">
            {FILTERS.map(f => {
              const count = f.id === 'all' ? docs.length : docs.filter(d => d.status === f.id).length
              return (
                <button key={f.id} type="button" onClick={() => setFilter(f.id)}
                  className={cn(
                    'px-3 py-2 rounded-xl text-xs font-medium cursor-pointer transition-colors whitespace-nowrap shrink-0 border',
                    filter === f.id
                      ? 'bg-indigo-600 text-white border-indigo-500 shadow-sm'
                      : 'bg-surface-elevated/70 text-text-muted hover:text-text-secondary border-border'
                  )}>{f.label}{isDecisionProject ? ` (${count})` : ''}</button>
              )
            })}
            {isDecisionProject && (
              <button type="button" onClick={() => setDecisionSort(s => s === 'number' ? 'title' : 'number')}
                className="ml-auto px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap shrink-0 border border-border bg-surface-elevated/70 text-text-secondary hover:border-indigo-500/30 transition-colors cursor-pointer">
                Sort: {decisionSort === 'number' ? 'AD Number' : 'Title'}
              </button>
            )}
          </div>

          {/* Decision library gets the richer grouped presentation; every other architecture folder keeps the generic document grid. */}
          {isDecisionProject ? (
            <div className="space-y-3">
              {loading ? (
                <div className="flex items-center gap-2 text-text-muted text-sm py-10 justify-center"><RefreshCw className="h-4 w-4 animate-spin" /> Loading decisions...</div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-10 text-text-muted text-sm">No decisions found</div>
              ) : DECISION_GROUPS.map(group => {
                const groupDocs = filtered.filter(d => group.ids.includes((decisionNumber(d) ?? -1) as never))
                if (groupDocs.length === 0) return null
                const GroupIcon = group.icon
                const collapsed = !!collapsedDecisionGroups[group.name]
                return (
                  <section key={group.name} className="rounded-2xl border border-border bg-surface-elevated/45 overflow-hidden shadow-sm">
                    <button type="button" onClick={() => setCollapsedDecisionGroups(v => ({ ...v, [group.name]: !v[group.name] }))}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-surface-elevated/60 transition-colors cursor-pointer">
                      <div className="w-9 h-9 rounded-xl bg-indigo-500/10 border border-indigo-500/15 flex items-center justify-center shrink-0"><GroupIcon className="h-4.5 w-4.5 text-indigo-400" /></div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-text-primary">{group.name}</div>
                        <div className="text-[11px] text-text-muted truncate">{group.description}</div>
                      </div>
                      <span className="text-[11px] text-text-muted whitespace-nowrap">{groupDocs.length} decision{groupDocs.length === 1 ? '' : 's'}</span>
                      <ChevronRight className={cn('h-4 w-4 text-text-muted transition-transform', !collapsed && 'rotate-90')} />
                    </button>
                    {!collapsed && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2.5 p-3 pt-0">
                        {groupDocs.map(doc => {
                          const edge = doc.status === 'approved' ? 'border-l-emerald-500' : doc.status === 'in_progress' ? 'border-l-amber-500' : 'border-l-slate-400'
                          const tagList = (doc.tags || '').split(',').map(t => t.trim()).filter(Boolean).slice(0, 2)
                          return (
                            <button key={doc.id} type="button" onClick={() => setActiveDoc(doc)}
                              className={cn('min-h-[170px] rounded-xl border border-border border-l-[3px] bg-surface/90 hover:bg-surface hover:border-indigo-500/25 p-3.5 text-left transition-all group cursor-pointer flex flex-col shadow-sm', edge)}>
                              <div className="flex items-center justify-between gap-2 mb-3">
                                <span className="text-xs font-bold text-text-primary tracking-wide">{decisionLabel(doc)}</span>
                                <DecisionStatusBadge status={doc.status} />
                              </div>
                              <h3 className="text-sm font-semibold text-text-primary leading-snug mb-1.5">{cleanDecisionTitle(doc)}</h3>
                              {doc.summary && <p className="text-[11px] text-text-muted leading-relaxed line-clamp-3 mb-2">{doc.summary}</p>}
                              {tagList.length > 0 && <div className="flex flex-wrap gap-1 mt-auto mb-2">{tagList.map(tag => <span key={tag} className="px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-500 dark:text-indigo-300 text-[9px]">{tag}</span>)}</div>}
                              <div className="flex items-center gap-1.5 mt-auto pt-2 text-[11px] text-text-muted border-t border-border/60">
                                <FileText className="h-3 w-3 text-indigo-400" /><span>v{doc.version}</span><ChevronRight className="h-3.5 w-3.5 ml-auto group-hover:text-indigo-400 transition-colors" />
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </section>
                )
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {loading ? (
                <div className="col-span-full flex items-center gap-2 text-text-muted text-sm py-10 justify-center"><RefreshCw className="h-4 w-4 animate-spin" /> Loading documents...</div>
              ) : filtered.length === 0 ? (
                <div className="col-span-full text-center py-10 text-text-muted text-sm">No documents found</div>
              ) : filtered.map(doc => {
                const edge = doc.status === 'approved' ? 'border-l-emerald-500' : doc.status === 'in_progress' ? 'border-l-amber-500' : 'border-l-slate-400'
                const tagList = (doc.tags || '').split(',').map(t => t.trim()).filter(Boolean).slice(0, 2)
                return (
                  <div key={doc.id} className={cn('min-h-[185px] rounded-xl border border-border border-l-[3px] bg-surface/90 hover:border-indigo-500/25 p-4 transition-all flex flex-col shadow-sm', edge)}>
                    <button type="button" onClick={() => setActiveDoc(doc)} className="text-left group cursor-pointer flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-500/10 border border-indigo-500/15 flex items-center justify-center shrink-0"><FileText className="h-4 w-4 text-indigo-400" /></div>
                        <div className="flex items-center gap-1.5">{doc.pinned === 1 && <Pin className="h-3 w-3 text-indigo-400" />}<StatusBadge status={doc.status} /></div>
                      </div>
                      <h3 className="text-sm font-semibold text-text-primary leading-snug mb-1.5 line-clamp-2">{doc.title}</h3>
                      {doc.summary && <p className="text-[11px] text-text-muted leading-relaxed line-clamp-3 mb-2">{doc.summary}</p>}
                      {tagList.length > 0 && <div className="flex flex-wrap gap-1 mb-2">{tagList.map(tag => <span key={tag} className="px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-500 dark:text-indigo-300 text-[9px]">{tag}</span>)}</div>}
                    </button>
                    <div className="flex items-center gap-2 pt-2 mt-auto border-t border-border/60 text-[10px] text-text-muted">
                      <span className="font-mono truncate">{doc.filename}</span><span className="ml-auto shrink-0">v{doc.version}</span>
                      {isTrash && <button type="button" onClick={() => restoreDoc(doc)} title="Restore" className="p-1.5 rounded-lg hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors cursor-pointer"><RotateCcw className="h-3.5 w-3.5" /></button>}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Architecture's internal folder library. The parent/root screen remains
// separate; this view is deliberately scoped to the folders inside Architecture.
function ArchitectureCategoriesView({ projects, loading, onBack, onSelect, onCreated }: {
  projects: ArchProject[]
  loading: boolean
  onBack: () => void
  onSelect: (p: ArchProject) => void
  onCreated: () => void
}) {
  const [folderSearch, setFolderSearch] = useState('')
  const [folderSort, setFolderSort] = useState<'name' | 'docs'>('name')
  const [showCreateFolder, setShowCreateFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [newFolderDescription, setNewFolderDescription] = useState('')
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [createFolderError, setCreateFolderError] = useState('')

  async function createFolder() {
    const name = newFolderName.trim()
    if (!name || creatingFolder) return
    setCreatingFolder(true)
    setCreateFolderError('')
    try {
      const data = await API('/architecture/projects', { method: 'POST', body: JSON.stringify({ name, description: newFolderDescription.trim() }) })
      if (!data.success) throw new Error(data.error || 'Could not create folder')
      setNewFolderName('')
      setNewFolderDescription('')
      setShowCreateFolder(false)
      onCreated()
    } catch (err) {
      setCreateFolderError(err instanceof Error ? err.message : 'Could not create folder')
    } finally {
      setCreatingFolder(false)
    }
  }
  const visibleProjects = projects
    .filter(p => !folderSearch || `${p.name} ${p.description}`.toLowerCase().includes(folderSearch.toLowerCase()))
    .sort((a, b) => folderSort === 'docs' ? b.doc_count - a.doc_count : a.name.localeCompare(b.name))

  return (
    <div className="space-y-5">
      <button type="button" onClick={onBack}
        className="flex items-center gap-1.5 text-text-muted hover:text-text-primary text-xs font-mono transition-colors cursor-pointer">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>

      <section className="relative overflow-hidden rounded-2xl border border-indigo-500/15 bg-gradient-to-r from-indigo-500/10 via-blue-500/5 to-transparent p-5 sm:p-6 shadow-sm">
        <div className="absolute -right-12 -top-16 h-48 w-48 rounded-full border border-indigo-400/10" />
        <div className="absolute right-8 top-5 h-28 w-28 rotate-45 rounded-3xl border border-indigo-400/10" />
        <div className="relative flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center shrink-0 shadow-sm">
            <Layers3 className="h-7 w-7 text-indigo-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-text-primary font-bold text-2xl">Architecture</h1>
              <span className="rounded-full border border-indigo-500/20 bg-indigo-500/10 px-2.5 py-1 text-[10px] font-semibold text-indigo-400">{projects.length} folders</span>
            </div>
            <p className="text-indigo-500 dark:text-indigo-300 text-sm mt-0.5">Your platform knowledge base</p>
            <p className="text-text-muted text-xs sm:text-sm mt-2 max-w-2xl leading-relaxed">Explore the architecture folders containing platform design, specifications, governance and technical documentation.</p>
          </div>
          <div className="hidden lg:flex items-center gap-2 text-[10px] font-mono text-emerald-400/80 border border-emerald-500/20 bg-emerald-500/5 rounded-xl px-3 py-2">
            <Unlock className="h-3 w-3" /> Architect Mode
          </div>
        </div>
      </section>

      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
        <input value={folderSearch} onChange={e => setFolderSearch(e.target.value)} placeholder="Search folders..."
          className="w-full bg-surface-elevated/90 border border-border rounded-xl pl-10 pr-4 py-3 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-indigo-500/40 shadow-sm" />
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <div className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm whitespace-nowrap">
          <FolderOpen className="h-3.5 w-3.5" /> All Folders ({visibleProjects.length})
        </div>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <button type="button" onClick={() => setShowCreateFolder(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-500 text-white shadow-sm transition-colors cursor-pointer whitespace-nowrap">
            <Plus className="h-3.5 w-3.5" /> New Folder
          </button>
          <button type="button" onClick={() => setFolderSort(s => s === 'name' ? 'docs' : 'name')}
            className="px-3 py-2 rounded-xl text-xs font-medium border border-border bg-surface-elevated/80 text-text-secondary hover:border-indigo-500/30 transition-colors cursor-pointer whitespace-nowrap">
            Sort: {folderSort === 'name' ? 'Name A–Z' : 'Document count'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {loading ? (
          <div className="col-span-full flex items-center gap-2 text-text-muted text-sm py-10 justify-center">
            <RefreshCw className="h-4 w-4 animate-spin" /> Loading folders...
          </div>
        ) : visibleProjects.length === 0 ? (
          <div className="col-span-full text-center py-10 text-text-muted text-sm">No folders found</div>
        ) : visibleProjects.map((p, index) => {
          const Icon = PROJECT_ICONS[p.icon] ?? FolderOpen
          return (
            <button key={p.id} type="button" onClick={() => onSelect(p)}
              className="min-h-[190px] rounded-2xl bg-surface-elevated/90 hover:bg-surface border border-border hover:border-indigo-500/25 p-4 text-left group cursor-pointer transition-all shadow-sm hover:shadow-md flex flex-col">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="relative">
                  <div className="w-11 h-9 rounded-lg rounded-tl-sm flex items-center justify-center border shadow-sm"
                    style={{ backgroundColor: `${p.color}20`, borderColor: `${p.color}35` }}>
                    <Icon className="h-5 w-5" style={{ color: p.color }} />
                  </div>
                  <div className="absolute -top-1 left-1 h-2 w-4 rounded-t-sm" style={{ backgroundColor: `${p.color}80` }} />
                </div>
                <span className="rounded-lg bg-indigo-500/8 border border-indigo-500/10 px-2 py-1 text-[10px] font-bold text-indigo-400 font-mono">{String(index + 1).padStart(2, '0')}</span>
              </div>
              <h3 className="text-sm font-semibold text-text-primary leading-snug mb-1.5 group-hover:text-indigo-400 transition-colors">{p.name}</h3>
              <p className="text-[11px] text-text-muted leading-relaxed line-clamp-3">{p.description}</p>
              <div className="flex items-center gap-2 mt-auto pt-4 text-[10px] text-text-muted border-t border-border/60">
                <FileText className="h-3.5 w-3.5 text-indigo-400" />
                <span>{p.doc_count} docs</span>
                <span className="ml-auto w-7 h-7 rounded-full border border-indigo-500/15 bg-indigo-500/5 flex items-center justify-center group-hover:bg-indigo-500/10 transition-colors">
                  <ChevronRight className="h-3.5 w-3.5 text-indigo-400" />
                </span>
              </div>
            </button>
          )
        })}
      </div>

      <div className="rounded-2xl border border-dashed border-indigo-500/25 bg-indigo-500/[0.03] p-5 flex flex-col sm:flex-row items-center justify-center gap-3 text-center sm:text-left">
        <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center"><BookOpen className="h-5 w-5 text-indigo-400" /></div>
        <div>
          <div className="text-text-primary text-sm font-semibold">Explore Documentation</div>
          <div className="text-text-muted text-xs mt-0.5">Select a folder to view its documents and architecture knowledge.</div>
        </div>
      </div>

      {showCreateFolder && createPortal(
        <div className="fixed inset-0 z-[120] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={e => { if (e.target === e.currentTarget) setShowCreateFolder(false) }}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-surface-elevated shadow-2xl p-5 sm:p-6">
            <div className="flex items-start justify-between gap-3 mb-5">
              <div>
                <h2 className="text-lg font-bold text-text-primary">Create New Folder</h2>
                <p className="text-xs text-text-muted mt-1">Create a new OPS architecture space. You can add or improve its description later.</p>
              </div>
              <button type="button" onClick={() => setShowCreateFolder(false)} className="p-1.5 rounded-lg hover:bg-surface text-text-muted cursor-pointer"><X className="h-4 w-4" /></button>
            </div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">Folder name</label>
            <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)} maxLength={80} placeholder="e.g. Billing Architecture"
              className="w-full rounded-xl border border-border bg-surface px-3.5 py-3 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-indigo-500/50" />
            <label className="block text-xs font-semibold text-text-secondary mt-4 mb-1.5">Description <span className="font-normal text-text-muted">(optional)</span></label>
            <textarea value={newFolderDescription} onChange={e => setNewFolderDescription(e.target.value)} rows={3} placeholder="What belongs in this folder?"
              className="w-full resize-none rounded-xl border border-border bg-surface px-3.5 py-3 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-indigo-500/50" />
            {createFolderError && <div className="mt-3 text-xs text-red-400">{createFolderError}</div>}
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setShowCreateFolder(false)} className="px-4 py-2.5 rounded-xl border border-border text-xs font-semibold text-text-secondary hover:bg-surface cursor-pointer">Cancel</button>
              <button type="button" disabled={!newFolderName.trim() || creatingFolder} onClick={createFolder}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white text-xs font-semibold cursor-pointer disabled:cursor-not-allowed">
                {creatingFolder ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Create Folder
              </button>
            </div>
          </div>
        </div>, document.body
      )}
    </div>
  )
}

// Root knowledge-space screen. The OPS display label is presentation-only:
// underlying architecture/ storage and project identities remain unchanged.
function RootView({ architectureDocCount, ecosystemProjects, loading, onOpenArchitecture, onSelectEcosystem }: {
  architectureDocCount: number
  ecosystemProjects: ArchProject[]
  loading: boolean
  onOpenArchitecture: () => void
  onSelectEcosystem: (p: ArchProject) => void
}) {
  const [rootSearch, setRootSearch] = useState('')
  const q = rootSearch.trim().toLowerCase()
  const showOps = !q || `ops the constitution blueprints decisions security governance`.includes(q)
  const visibleEcosystems = ecosystemProjects.filter(p => !q || `${p.name} ${p.description}`.toLowerCase().includes(q))

  return (
    <div className="space-y-5">
      <section className="relative overflow-hidden rounded-2xl border border-indigo-500/15 bg-gradient-to-r from-indigo-500/10 via-violet-500/[0.06] to-transparent min-h-[205px] shadow-sm">
        <div className="absolute inset-0 opacity-70 bg-[radial-gradient(circle_at_65%_40%,rgba(99,102,241,0.16),transparent_32%)]" />
        <div className="relative p-6 sm:p-8 lg:p-10 max-w-4xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.30em] text-indigo-500 mb-3">Knowledge Base</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-text-primary">OPS Architecture</h1>
          <p className="text-base sm:text-lg font-medium text-slate-600 dark:text-slate-300 mt-2">Everything OPS knows about itself and what it maintains.</p>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed mt-4 max-w-2xl">The constitution — blueprints, decisions, security, governance, and everything else that defines how OPS is built.</p>
        </div>
        <div className="absolute right-5 top-6 hidden xl:block max-w-[180px] text-right">
          <p className="text-xs italic leading-relaxed text-indigo-500 dark:text-indigo-300">“Documented systems build better futures.”</p>
          <p className="text-[10px] text-text-muted mt-2">— OPS by BakersGuild</p>
        </div>
      </section>

      <div className="flex flex-col sm:flex-row gap-2.5 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
          <input value={rootSearch} onChange={e => setRootSearch(e.target.value)} placeholder="Search folders, documents, or keywords..."
            className="w-full bg-surface-elevated/90 border border-border rounded-xl pl-10 pr-4 py-3 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-indigo-500/40 shadow-sm" />
        </div>
        <div className="px-3 py-3 rounded-xl border border-border bg-surface-elevated/80 text-xs text-text-muted whitespace-nowrap">4 knowledge spaces</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3.5">
        {showOps && (
          <button type="button" onClick={onOpenArchitecture}
            className="min-h-[205px] rounded-2xl bg-surface-elevated/90 hover:bg-surface border border-indigo-500/20 hover:border-indigo-500/40 p-5 text-left group cursor-pointer transition-all shadow-sm hover:shadow-md flex flex-col">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-indigo-500/10 border border-indigo-500/20">
                <Building2 className="h-6 w-6 text-indigo-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-base font-bold text-text-primary group-hover:text-indigo-400 transition-colors">OPS</h2>
                  <span className="rounded-lg bg-indigo-500/10 border border-indigo-500/15 px-2 py-1 text-[10px] font-semibold text-indigo-400">{architectureDocCount} docs</span>
                </div>
                <p className="text-xs text-text-muted leading-relaxed mt-2">The constitution — blueprints, decisions, security, governance, and everything else that defines how OPS is built.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-auto pt-4 border-t border-border/60 text-[10px] text-text-muted">
              <FolderOpen className="h-3.5 w-3.5 text-indigo-400" /><span>Architecture knowledge</span>
              <span className="ml-auto w-8 h-8 rounded-full border border-indigo-500/15 bg-indigo-500/5 flex items-center justify-center group-hover:bg-indigo-500/10"><ChevronRight className="h-4 w-4 text-indigo-400" /></span>
            </div>
          </button>
        )}

        {loading ? (
          <div className="col-span-full flex items-center gap-2 text-text-muted text-sm py-8 justify-center"><RefreshCw className="h-4 w-4 animate-spin" /> Loading...</div>
        ) : visibleEcosystems.map(p => {
          const Icon = PROJECT_ICONS[p.icon] ?? Hexagon
          return (
            <button key={p.id} type="button" onClick={() => onSelectEcosystem(p)}
              className="min-h-[205px] rounded-2xl bg-surface-elevated/90 hover:bg-surface border border-border hover:border-indigo-500/25 p-5 text-left group cursor-pointer transition-all shadow-sm hover:shadow-md flex flex-col">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border" style={{ backgroundColor: `${p.color}15`, borderColor: `${p.color}25` }}>
                  <Icon className="h-6 w-6" style={{ color: p.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-base font-bold text-text-primary group-hover:text-indigo-400 transition-colors">{p.name}</h2>
                    <span className="rounded-lg bg-indigo-500/8 border border-indigo-500/10 px-2 py-1 text-[10px] font-semibold text-text-muted">{p.doc_count} docs</span>
                  </div>
                  <p className="text-xs text-text-muted leading-relaxed mt-2 line-clamp-4">{p.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-auto pt-4 border-t border-border/60 text-[10px] text-text-muted">
                <FolderOpen className="h-3.5 w-3.5" style={{ color: p.color }} /><span>Architecture knowledge</span>
                <span className="ml-auto w-8 h-8 rounded-full border border-border bg-surface flex items-center justify-center group-hover:border-indigo-500/25"><ChevronRight className="h-4 w-4 text-indigo-400" /></span>
              </div>
            </button>
          )
        })}
        {!loading && !showOps && visibleEcosystems.length === 0 && <div className="col-span-full py-10 text-center text-sm text-text-muted">No knowledge spaces found</div>}
      </div>
    </div>
  )
}

// Main export -- PIN gate fires immediately on panel load
export function ArchitecturePanel() {
  const [unlocked, setUnlocked]         = useState(false)
  const [projects, setProjects]         = useState<ArchProject[]>([])
  const [loading, setLoading]           = useState(false)
  const [view, setView]                 = useState<'root' | 'architecture' | 'project'>('root')
  const [activeProject, setActiveProject] = useState<ArchProject | null>(null)

  const loadProjects = useCallback(() => {
    setLoading(true)
    API('/architecture/projects')
      .then(r => setProjects(r.projects ?? []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (unlocked) loadProjects()
  }, [unlocked, loadProjects])

  const architectureProjects = projects.filter(p => !ECOSYSTEM_PROJECT_IDS.includes(p.id))
  const ecosystemProjects = projects.filter(p => ECOSYSTEM_PROJECT_IDS.includes(p.id))

  // Step 1: PIN gate -- before anything else renders
  if (!unlocked) {
    return <PinGate onAuth={() => setUnlocked(true)} />
  }

  // Step 2: Project detail (reachable from either Architecture categories or an ecosystem folder)
  if (view === 'project' && activeProject) {
    return (
      <ProjectView
        project={activeProject}
        onBack={() => {
          setActiveProject(null)
          setView(ECOSYSTEM_PROJECT_IDS.includes(activeProject.id) ? 'root' : 'architecture')
        }}
      />
    )
  }

  // Step 3: Architecture internal folders, one level inside the wrapper
  if (view === 'architecture') {
    return (
      <ArchitectureCategoriesView
        projects={architectureProjects}
        loading={loading}
        onBack={() => setView('root')}
        onSelect={p => { setActiveProject(p); setView('project') }}
        onCreated={loadProjects}
      />
    )
  }

  // Step 4 (default): root -- existing Architecture Vault navigation first,
  // followed by the separate live R2 Knowledge Explorer. The catalogue/folder
  // mechanism above remains unchanged; the explorer is an additional view.
  return (
    <div className="space-y-8">
      <RootView
        architectureDocCount={architectureProjects.reduce((s, p) => s + p.doc_count, 0)}
        ecosystemProjects={ecosystemProjects}
        loading={loading}
        onOpenArchitecture={() => setView('architecture')}
        onSelectEcosystem={p => { setActiveProject(p); setView('project') }}
      />
      <KnowledgeExplorer />
    </div>
  )
}
